import { Fragment, useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import {
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  TableContainer,
  CircularProgress,
  ImageList,
  ImageListItem,
  Typography,
  IconButton,
  Modal,
  Snackbar,
  Alert,
} from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import RemoveRedEyeOutlined from "@mui/icons-material/RemoveRedEyeOutlined";
import {
  deleteFilePatient,
  getSectionsUserProgram,
} from "../../../api/user-program";
import { ISection } from "../../../interface/ISections";
import { formatTimestamp } from "../../../utils/date";
import useSnackbar from "../../../hooks/useSnackbar";
import useFileDownloader from "../../../hooks/useFileDownloader";
import { DeletePostModal } from "../modal-delete";
import { useDocxViewer } from "../../../hooks/useDocxViewer";

export const DonwloadFile = () => {
  const [sections, setSections] = useState<ISection[]>([]);
  const { patient } = useParams() as { patient: string };
  const [loading, setLoading] = useState(false);
  const { message, severity, open, showSnackbar, closeSnackbar } =
    useSnackbar();

  const {
    handleDownloadClicked,
    handleDownloadTemporaryLink,
    setIsFileDetailSelected,
    isFileDetailSelected,
    temporaryUrl,
    isAPdfFile,
    isDocxFile,
  } = useFileDownloader();

  const docxContent = useDocxViewer(temporaryUrl);

  const fetchSections = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getSectionsUserProgram(patient);
      setSections(response);
    } catch (error: any) {
      console.error(
        "Error fetching sections:",
        error.response.data.message || error.message
      );
    } finally {
      setLoading(false);
    }
  }, [patient]);

  const deleteFile = async (patientId: string, documentId: string) => {
    try {
      await deleteFilePatient(patientId, documentId);
      fetchSections();
      showSnackbar("Dokument erfolgreich gelöscht", "success");
    } catch (error: any) {
      console.error(
        "Error deleting file:",
        error.response.data.message || error.message
      );
      showSnackbar("Fehler beim Löschen des Dokuments", "error");
    }
  };

  const handleDetailClose = () => {
    setIsFileDetailSelected(false);
  };

  useEffect(() => {
    fetchSections();
  }, [fetchSections]);

  return (
    <Box mt={5} sx={{ width: "175vh" }}>
      <Paper>
        <TableContainer>
          {loading ? (
            <Box
              mt="250px"
              display="flex"
              alignContent="center"
              justifyContent="center"
              height="500px"
            >
              <CircularProgress size={100} />
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: "16px", fontWeight: "bold" }}>
                    Titel
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      fontSize: "16px",
                      fontWeight: "bold",
                    }}
                  >
                    Kategorie
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      fontSize: "16px",
                      fontWeight: "bold",
                    }}
                  >
                    Erstellungsdatum
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      fontSize: "16px",
                      fontWeight: "bold",
                    }}
                  >
                    Aktion
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sections.length === 0 ||
                sections.every((section) =>
                  section.documents.every((document) => document.hidden)
                ) ? (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Typography color="red">
                        Es sind keine Dokumente vorhanden
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sections.map((section, index) => (
                    <Fragment key={index}>
                      {section.documents.map(
                        (document, documentIndex) =>
                          !document.hidden && (
                            <TableRow key={documentIndex}>
                              <TableCell
                                width="28%"
                                sx={{ fontWeight: "bold" }}
                              >
                                {document.title}
                              </TableCell>
                              <TableCell sx={{ textAlign: "center" }}>
                                {document.category}
                              </TableCell>
                              <TableCell sx={{ textAlign: "center" }}>
                                {formatTimestamp(document.createdOn)}
                              </TableCell>

                              <TableCell sx={{ textAlign: "center" }}>
                                <Box
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <IconButton
                                    sx={{ marginTop: "10px", padding: "10px" }}
                                    onClick={() =>
                                      handleDownloadClicked(document)
                                    }
                                  >
                                    <RemoveRedEyeOutlined
                                      sx={{
                                        fontWeight: "bold",
                                        color: "black",
                                      }}
                                    />
                                  </IconButton>

                                  <DeletePostModal
                                    onDelete={() => {
                                      deleteFile(patient, document._id);
                                    }}
                                  />
                                </Box>
                              </TableCell>
                            </TableRow>
                          )
                      )}
                    </Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Paper>

      <Modal open={isFileDetailSelected} onClose={handleDetailClose}>
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "white",
            border: "2px solid #000",
            boxShadow: 24,
            p: 4,
            borderRadius: 5,
            width: "900px",
            height: "600px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <IconButton
              onClick={() => handleDownloadTemporaryLink(temporaryUrl)}
              sx={{ marginTop: "10px", padding: "10px" }}
            >
              <CloudDownloadIcon
                sx={{ fontWeight: "bold", color: "black" }}
                fontSize="medium"
              />
            </IconButton>
          </Box>
          {isAPdfFile ? (
            <iframe
              title="file"
              src={temporaryUrl}
              width="100%"
              height="500px"
            />
          ) : isDocxFile ? (
            <Box
              style={{
                width: "100%",
                height: "500px",
                overflowY: "auto",
              }}
              dangerouslySetInnerHTML={{
                __html: docxContent || "Loading...",
              }}
            />
          ) : (
            <ImageList
              sx={{ width: "100%", height: 500 }}
              cols={1}
              rowHeight={450}
            >
              <ImageListItem>
                <img
                  srcSet={temporaryUrl}
                  src={temporaryUrl}
                  alt={"File"}
                  loading="lazy"
                />
              </ImageListItem>
            </ImageList>
          )}
        </Box>
      </Modal>
      <Snackbar
        open={open}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert severity={severity} variant="filled" onClose={closeSnackbar}>
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
